import { z } from 'zod';
import { ssoDomainNameSchema } from './sso.js';
/**
 * THE TENANT'S ALLOWED EMAIL DOMAINS (handoff-0017 §2). A free-form address on a location's
 * notification list must be on one of them, so an administrator — or a compromised one —
 * cannot route every visitor's name and arrival time to an outside inbox.
 *
 * Seeded from the owner's signup email (`SIGNUP`) and extended by an administrator (`ADMIN`).
 * Free mail is NEVER allowed, by either path: a shared consumer domain is not "the company".
 */
/**
 * How a domain got on the list. Must stay identical — same members, SAME ORDER — to the
 * `TenantEmailDomainSource` Prisma enum (ADR-0011), proven by
 * packages/db/src/notification-enums.test.ts.
 */
export const TENANT_EMAIL_DOMAIN_SOURCES = ['SIGNUP', 'ADMIN'];
export const tenantEmailDomainSourceSchema = z.enum(TENANT_EMAIL_DOMAIN_SOURCES);
/**
 * Consumer mailbox providers — never a tenant's own domain. Exact names; the families below
 * catch the country variants (`gmx.de`, `yahoo.co.uk`). Mirrored, for the one-off backfill, in
 * migration `20261024002927_location_notification_recipients_and_email_domains`; this constant
 * is the live source of truth.
 */
export const FREE_MAIL_DOMAINS = new Set([
    'gmail.com',
    'googlemail.com',
    'outlook.com',
    'hotmail.com',
    'live.com',
    'msn.com',
    'passport.com',
    'yahoo.com',
    'ymail.com',
    'rocketmail.com',
    'icloud.com',
    'me.com',
    'mac.com',
    'aol.com',
    'aim.com',
    'proton.me',
    'protonmail.com',
    'protonmail.ch',
    'pm.me',
    'mail.com',
    'email.com',
    'zoho.com',
    'zohomail.com',
    'yandex.com',
    'yandex.ru',
    'ya.ru',
    'mail.ru',
    'inbox.ru',
    'list.ru',
    'bk.ru',
    'gmx.com',
    'gmx.net',
    'gmx.de',
    'web.de',
    't-online.de',
    'fastmail.com',
    'fastmail.fm',
    'hey.com',
    'tutanota.com',
    'tutanota.de',
    'tuta.io',
    'tuta.com',
    'qq.com',
    '163.com',
    '126.com',
    'yeah.net',
    'sina.com',
    'naver.com',
    'daum.net',
    'hanmail.net',
    'rediffmail.com',
    'libero.it',
    'laposte.net',
    'orange.fr',
    'free.fr',
    'wanadoo.fr',
    'comcast.net',
    'verizon.net',
    'att.net',
    'sbcglobal.net',
    'cox.net',
    'btinternet.com',
    'sky.com',
    'virginmedia.com',
    'duck.com',
    'hushmail.com',
]);
/**
 * Provider names blocked under ANY country TLD: `<family>.<tld>` or `<family>.<co|com|net|org>.<cc>`
 * (`gmx.at`, `yahoo.co.jp`, `outlook.com.br`). Deliberately not "any domain whose first label is
 * one of these": `live.acme.com` is Acme's subdomain, not Microsoft's.
 */
export const FREE_MAIL_DOMAIN_FAMILIES = new Set([
    'gmx',
    'yandex',
    'yahoo',
    'hotmail',
    'outlook',
    'live',
    'aol',
    'zoho',
    'mail',
    'proton',
    'protonmail',
    'icloud',
]);
const FAMILY_SECOND_LEVELS = new Set(['co', 'com', 'net', 'org']);
const COUNTRY_OR_SHORT_TLD = /^[a-z]{2,3}$/;
/** Whether a NORMALISED domain (see `normalizeEmailDomain`) is a consumer mailbox provider. */
export function isFreeMailDomain(domain) {
    if (FREE_MAIL_DOMAINS.has(domain))
        return true;
    const labels = domain.split('.');
    const [family, second, third] = labels;
    if (family === undefined || !FREE_MAIL_DOMAIN_FAMILIES.has(family))
        return false;
    if (labels.length === 2)
        return second !== undefined && COUNTRY_OR_SHORT_TLD.test(second);
    if (labels.length === 3) {
        return (second !== undefined &&
            FAMILY_SECOND_LEVELS.has(second) &&
            third !== undefined &&
            COUNTRY_OR_SHORT_TLD.test(third));
    }
    return false;
}
/**
 * The WHATWG `URL` constructor, present in every browser and in Node, read off `globalThis`
 * because this package compiles with neither the DOM nor the Node type libraries (it is
 * dependency-free, docs/002). Absent only in an exotic runtime, where IDNs then fail the format
 * check rather than being converted.
 */
const UrlParser = globalThis.URL;
/**
 * Lower-case, trim, drop a leading `@`, and convert an internationalised name to punycode
 * (`bücher.de` → `xn--bcher-kva.de`) with the WHATWG URL parser every runtime ships — no
 * dependency. Input the parser rejects is returned lower-cased and unconverted, so the format
 * check that follows refuses it rather than this function guessing.
 */
export function normalizeEmailDomain(value) {
    const stripped = value.trim().replace(/^@+/, '').toLowerCase();
    if (stripped === '' || UrlParser === undefined)
        return stripped;
    try {
        return new UrlParser(`http://${stripped}`).hostname;
    }
    catch {
        return stripped;
    }
}
/** The domain part of an address, normalised. `''` when there is no `@`. */
export function emailDomainOf(email) {
    const at = email.lastIndexOf('@');
    return at === -1 ? '' : normalizeEmailDomain(email.slice(at + 1));
}
/**
 * A domain as an administrator types it (`@Acme.com`, `bücher.de`), normalised and then held to
 * the same DNS-name rule as a company sign-in domain (`ssoDomainNameSchema`). Free mail is NOT
 * refused here — the route answers it with its own error code (`EMAIL_DOMAIN_FREE_MAIL`), which
 * a format error would hide.
 */
export const tenantEmailDomainNameSchema = z
    .string()
    .max(300)
    .transform((value) => normalizeEmailDomain(value))
    .pipe(ssoDomainNameSchema);
/** `GET /api/web/tenant/email-domains` row. */
export const tenantEmailDomainDTOSchema = z
    .object({
    id: z.string().min(1),
    domain: z.string().min(1),
    source: tenantEmailDomainSourceSchema,
    createdAt: z.string().datetime(),
})
    .strict();
/** `POST /api/web/tenant/email-domains` body. The source is decided by the server (`ADMIN`). */
export const addTenantEmailDomainSchema = z
    .object({ domain: tenantEmailDomainNameSchema })
    .strict();
//# sourceMappingURL=tenant-email-domain.js.map